import { getDatesAnalyticsInfo } from "@cruisen/react-shared"
import { load } from "./storage"
import { getAuth } from "firebase/auth"
import { analytics } from "../services/firebase"
import { logEvent as logEventFirebase, setAnalyticsCollectionEnabled } from "firebase/analytics"
import { callFirebaseFunction } from "../services/callFirebaseFunction"
import { AnalyticsEventPayload } from "@cruisen/contract"
import Config from "../config"
import { signOut } from "../services/auth"

export const handleAsync = (func: () => Promise<void>, methodName: string) => {
  func()
    .then(() => console.debug(`[Analytics] ${methodName}`))
    .catch((error) => console.error(`[Analytics] Failed to log ${methodName}`, error))
}

class Analytics {
  private getUserId() {
    const currentUser = getAuth().currentUser
    return currentUser ? currentUser.uid : null
  }

  async configureAnalyticsCollection() {
    try {
      const currentUser = getAuth().currentUser
      const email = currentUser ? currentUser.email : null

      if (analytics) {
        setAnalyticsCollectionEnabled(analytics, !(email && email.endsWith("cruisen.com")))
      }
    } catch (error) {
      console.error(`[Analytics] Failed to set analytics collection`, error)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private logEvent(eventName: string, params: Record<string, any>) {
    handleAsync(async () => {
      const googleAnalyticsParams = { ...params }

      const userId = this.getUserId()
      if (userId) {
        googleAnalyticsParams.userId = userId
      }

      const lastViewedTripId = await load("lastViewedTrip")
      if (lastViewedTripId) {
        googleAnalyticsParams.tripId = lastViewedTripId
      }

      if (analytics) {
        logEventFirebase(analytics, eventName, {
          ...googleAnalyticsParams,
          userId,
        })
      }

      const trackEventResult = await callFirebaseFunction({
        name: "trackAnalyticsEvent",
        payload: {
          event: {
            eventTimestamp: new Date().toISOString(),
            eventName,
            platform: "web",
            appVersion: Config.appVersion,
            userId,
            tripId: lastViewedTripId,
            screenName: params.screenName || undefined,
            params: {
              ...params,
              isAdvisor: true, // TODO: change this if we add web support for users
            },
          } as AnalyticsEventPayload,
        },
      })

      if (trackEventResult.data?.details?.isTokenExpired === true) {
        await signOut()
        window.location.href = "/"
      }
    }, eventName)
  }

  logCrash(error: Error) {
    this.logEvent("Crash", { error })
  }

  logUpdateCruiseSearch({
    destinationRegions,
    originPortGroups,
    months,
  }: {
    destinationRegions: string
    originPortGroups: string
    months: number[]
  }) {
    const { startDate, endDate } = getDatesAnalyticsInfo(months)
    this.logEvent("CruiseSearchUpdate", {
      originPortGroups,
      destinationRegions,
      startDate,
      endDate,
    })
  }

  logViewItem({ name, id }: { name: string; id: string }) {
    this.logEvent("ViewCruise", {
      cruiseName: name,
      cruiseId: id,
    })
  }

  logTripCreation({
    name,
    destinationRegions,
    originPortGroups,
    months,
  }: {
    name: string
    destinationRegions: string
    originPortGroups: string
    months: number[]
  }) {
    const { startDate, endDate } = getDatesAnalyticsInfo(months)
    this.logEvent("TripCreation", {
      tripName: name,
      destinationRegions,
      originPortGroups,
      startDate,
      endDate,
    })
  }

  logAskAI() {
    this.logEvent("AskAI", {})
  }

  logLikeCruise(cruiseId: string) {
    this.logEvent("LikeCruise", { cruiseId })
  }

  logSignUp(params: Record<string, string>) {
    this.logEvent("SignUp", params)
  }

  logLogin() {
    this.logEvent("Login", {})
  }

  logBookCruise({ tripId, cruiseId }: { tripId: string; cruiseId: string }) {
    this.logEvent("BookCruise", { tripId, cruiseId })
  }

  logShare(tripId: string) {
    this.logEvent("Share", { trip_id: tripId })
  }

  logSetPreferredCruise({ cruiseId }: { cruiseId: string }) {
    this.logEvent("SetPreferredCruise", { cruiseId })
  }

  logSignOut() {
    this.logEvent("SignOut", {})
  }
}

const analyticsInstance = new Analytics()
export { analyticsInstance as Analytics }
